import React from "react";
import { observer } from "mobx-react";
import {
  Button,
  Center,
  Checkbox,
  Divider,
  HStack,
  Link,
  ListItem,
  OrderedList,
  Stack,
  Text,
  Image,
} from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import { CanadaMap } from "@/components/CanadaMap";
import { renderInnerHtml } from "@/helpers/dom";
import { ScrollToElement } from "@/components/ScrollToElement";
import type { OrderCheckboxData } from "@/store/types/order";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { H3 } from "@/components/Heading";
import { AppLink } from "@/routing/AppLink";
import { OrderUnitDetails } from "./UnitDetails";
import { ExtraUnitsCallout } from "@/components/ExtraUnitsCallout";
import { telemetry } from "@/telemetry";

export const OrderDetailsOntario = observer(() => {
  const {
    activeLanguage,
    isIndigenousFlow,
    orderData,
    setOrderData,
    isValidOrderMinimum,
    orderCheckboxData,
    toggleOrderCheckbox,
    showLeavingModal,
    ga4Store,
    telemetryStore,
  } = useStore();

  const language = translations.pages["order-details-ontario"][activeLanguage];
  const imageTranslations = translations.common.images[activeLanguage];

  const handleOshaLink = () => {
    telemetry.trackEvent("reference_link_clicked", { link: "osha" });
    showLeavingModal(
      "https://www.ontario.ca/page/naloxone-workplace#section-1",
    );
  };

  const handleStatuteLink = () => {
    telemetry.trackEvent("reference_link_clicked", { link: "ontario_statute" });
    showLeavingModal("https://www.ontario.ca/laws/statute/90o01#BK49");
  };

  const handleCheckboxChange = (key: keyof OrderCheckboxData) =>
    toggleOrderCheckbox(key);

  const handleCheckboxEmailUpdates = (value: boolean) => {
    setOrderData({
      ...orderData,
      subscribeToEmailUpdates: value,
    });
  };

  const navigate = useAppNavigate();

  const handleSubmit = () => {
    if (!isValid) {
      telemetryStore.detailsContinueBlocked([
        "agreeToPrivacyPolicy",
        "agreeToTerms",
        "confirmAge",
        "agreeToShareMyData",
        "agreeToTraining",
        "agreeToFairUse",
      ]);
      return;
    }

    telemetryStore.detailsSubmitted();
    ga4Store.addToCart();
    navigate({ to: "/order/shipping" });
  };

  const hasExtraUnitCostsCallout =
    !isIndigenousFlow && location.pathname.includes("details");

  const isValid =
    orderCheckboxData.agreeToPrivacyPolicy &&
    orderCheckboxData.agreeToTerms &&
    orderCheckboxData.confirmAge &&
    orderCheckboxData.agreeToShareMyData &&
    orderCheckboxData.agreeToTraining &&
    orderCheckboxData.agreeToFairUse &&
    isValidOrderMinimum;

  return (
    <Stack spacing="14">
      <ScrollToElement />
      <CanadaMap svgProps={{ height: "350" }} />
      <H3
        alignSelf="center"
        textAlign="center"
        maxW="container.md"
      >
        {renderInnerHtml(language.ultimate_consumers)}
        <Text
          as="span"
          color="primary-light"
        >
          {language.ontario_label}
        </Text>
        {renderInnerHtml(language.are_eligible)}
      </H3>

      {hasExtraUnitCostsCallout && <ExtraUnitsCallout />}
      <Divider />

      <Stack spacing="8">
        <H3 textAlign="center">
          {renderInnerHtml(language.note_registered_businesses)}
        </H3>
        <Stack>
          <Text fontWeight="bold">
            {renderInnerHtml(language.as_of_date)}
            <Link
              as="span"
              textDecoration="underline"
              onClick={handleStatuteLink}
            >
              {renderInnerHtml(language.occupational_health)}
            </Link>
            {renderInnerHtml(language.ohsa_update)}
          </Text>
          <Text>{renderInnerHtml(language.ohsa_require_employers)}</Text>
          <Text>{renderInnerHtml(language.those_employers)}</Text>
          <OrderedList pl="4">
            <ListItem>{renderInnerHtml(language.works_vicinity)}</ListItem>
            <ListItem>
              {renderInnerHtml(language.has_received_training)}
            </ListItem>
          </OrderedList>
        </Stack>
        <Center>
          <Button
            onClick={handleOshaLink}
            whiteSpace="normal"
            wordBreak="break-word"
          >
            {renderInnerHtml(language.click_ohsa_update)}
          </Button>
        </Center>
      </Stack>

      <Stack
        p="14"
        spacing="14"
        border="solid 1px white"
        borderRadius="6px"
        textAlign="center"
        alignItems="center"
        justifyContent="center"
      >
        <H3>{renderInnerHtml(language.video_access_header)}</H3>
        <Stack
          spacing="8"
          maxWidth="500px"
        >
          <Image
            src={imageTranslations.training_video}
            alt="Training video preview"
          />
          <AppLink to="/training">
            <Button
              whiteSpace="normal"
              wordBreak="break-word"
            >
              {language.video_access_button}
            </Button>
          </AppLink>
        </Stack>
      </Stack>

      <OrderUnitDetails />

      <Stack spacing="6">
        <Text fontWeight="bold">{renderInnerHtml(language.required)}</Text>
        <Stack>
          <Checkbox
            id="checkbox1"
            isChecked={orderCheckboxData.agreeToPrivacyPolicy}
            onChange={() => handleCheckboxChange("agreeToPrivacyPolicy")}
          >
            {renderInnerHtml(language.i_agree)}
            <Link
              href="https://www.emergentbiosolutions.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              textDecoration="underline"
            >
              {renderInnerHtml(language.privacy_policy)}
            </Link>
            .
          </Checkbox>

          <Checkbox
            id="checkbox2"
            isChecked={orderCheckboxData.agreeToTerms}
            onChange={() => handleCheckboxChange("agreeToTerms")}
          >
            {renderInnerHtml(language.i_acknowledge)}
            <Link
              as={AppLink}
              to="/general-terms"
              target="_blank"
              rel="noopener noreferrer"
              textDecoration="underline"
            >
              {language.general_terms_sale}
            </Link>
            .
          </Checkbox>

          <Checkbox
            id="checkbox3"
            isChecked={orderCheckboxData.confirmAge}
            onChange={() => handleCheckboxChange("confirmAge")}
          >
            {renderInnerHtml(language.i_confirm_18)}
          </Checkbox>

          <Checkbox
            id="checkbox4"
            isChecked={orderCheckboxData.agreeToTraining}
            onChange={() => handleCheckboxChange("agreeToTraining")}
          >
            {renderInnerHtml(language.i_attest)}
          </Checkbox>

          <Checkbox
            id="checkbox5"
            isChecked={orderCheckboxData.agreeToShareMyData}
            onChange={() => handleCheckboxChange("agreeToShareMyData")}
          >
            {renderInnerHtml(language.i_acknowledge_ordering_process)}
          </Checkbox>

          <Checkbox
            id="checkbox6"
            isChecked={orderCheckboxData.agreeToFairUse}
            onChange={() => handleCheckboxChange("agreeToFairUse")}
          >
            {renderInnerHtml(language.i_affirm)}
          </Checkbox>
        </Stack>
      </Stack>

      <Stack spacing="6">
        <Text fontWeight="bold">{language.optional}</Text>
        <Checkbox
          id="emailCheckbox"
          isChecked={orderData.subscribeToEmailUpdates}
          onChange={() =>
            handleCheckboxEmailUpdates(!orderData.subscribeToEmailUpdates)
          }
        >
          {renderInnerHtml(language.i_like_emails)}
        </Checkbox>
      </Stack>

      {!isIndigenousFlow && <Text>{renderInnerHtml(language.order_note)}</Text>}

      <HStack justifyContent="flex-end">
        <Button
          onClick={handleSubmit}
          aria-disabled={!isValid}
          whiteSpace="normal"
          wordBreak="break-word"
        >
          {language.proceed_billing_shipping}
        </Button>
      </HStack>
    </Stack>
  );
});
